import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle, Trash, Sparkle } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

const KIND_TONE = {
  approval: "bg-blue-100 text-blue-700",
  compliance: "bg-amber-100 text-amber-700",
  deliverable: "bg-blue-100 text-blue-700",
  payroll: "bg-emerald-100 text-emerald-700",
  asset: "bg-amber-100 text-amber-700",
  appointment: "bg-emerald-100 text-emerald-700",
  task: "bg-slate-100 text-slate-700",
  finance: "bg-blue-100 text-blue-700",
  system: "bg-slate-100 text-slate-600",
};

const SEV_DOT = {
  critical: "bg-red-500",
  warning: "bg-amber-500",
  success: "bg-emerald-500",
  info: "bg-blue-500",
};

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export default function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const polledOnce = useRef(false);

  const fetchUnreadCount = async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data.unread_count || 0);
    } catch { /* silent */ }
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 30 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  // First load — sync system signals once + initial counter
  useEffect(() => {
    let active = true;
    (async () => {
      if (!polledOnce.current) {
        polledOnce.current = true;
        try { await api.post("/notifications/sync-system-signals"); } catch { /* ignore */ }
      }
      if (active) await fetchUnreadCount();
    })();
    const id = setInterval(fetchUnreadCount, 60_000);
    return () => { active = false; clearInterval(id); };
  }, []);

  useEffect(() => { if (open) fetchList(); /* eslint-disable-next-line */ }, [open]);

  const onItemClick = async (n) => {
    if (!n.read_at) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    if (n.link) {
      // Strip the /admin prefix because router basename adds it
      const link = n.link.startsWith("/admin") ? n.link.slice(6) : n.link;
      navigate(link || "/");
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setUnread(0);
      setItems((rows) => rows.map((r) => ({ ...r, read_at: r.read_at || new Date().toISOString() })));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}`);
      setItems((rows) => rows.filter((r) => r.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          aria-label="Notifications"
          data-testid="notifications-bell"
        >
          <Bell size={18} weight={unread > 0 ? "fill" : "regular"} />
          {unread > 0 && (
            <span
              className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 font-mono-alt text-[10px] font-bold text-white"
              data-testid="notifications-unread-count"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0" data-testid="notifications-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div>
            <div className="font-display text-sm font-semibold text-slate-900">Notifications</div>
            <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
              {unread} unread · {items.length} total
            </div>
          </div>
          <div className="flex gap-1">
            {unread > 0 && (
              <Button size="sm" variant="ghost" onClick={markAllRead} data-testid="mark-all-read">
                <CheckCircle size={14} className="mr-1" /> Mark all read
              </Button>
            )}
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && <div className="px-4 py-6 text-center font-mono-alt text-xs uppercase tracking-widest text-slate-500">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="px-4 py-10 text-center">
              <Sparkle size={26} className="mx-auto text-slate-300" />
              <div className="mt-2 text-sm font-medium text-slate-700">All caught up</div>
              <div className="mt-1 text-xs text-slate-500">No new notifications. Take a breath.</div>
            </div>
          )}
          <ul className="divide-y divide-slate-100">
            {items.map((n) => (
              <li key={n.id}>
                <div
                  onClick={() => onItemClick(n)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") onItemClick(n); }}
                  className={`group flex w-full cursor-pointer gap-3 px-4 py-3 text-left transition hover:bg-slate-50 ${!n.read_at ? "bg-blue-50/40" : ""}`}
                  data-testid={`notification-${n.id}`}
                >
                  <span className={`mt-1 inline-block h-2 w-2 flex-shrink-0 rounded-full ${SEV_DOT[n.severity] || SEV_DOT.info}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-mono-alt rounded px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${KIND_TONE[n.kind] || KIND_TONE.system}`}>
                        {n.kind}
                      </span>
                      <span className="ml-auto font-mono-alt text-[10px] text-slate-400">{timeAgo(n.created_at)}</span>
                    </div>
                    <div className="mt-1 truncate text-sm font-semibold text-slate-900">{n.title}</div>
                    {n.body && <div className="mt-0.5 line-clamp-2 text-xs text-slate-500">{n.body}</div>}
                  </div>
                  <button
                    onClick={(e) => remove(e, n.id)}
                    className="opacity-0 group-hover:opacity-100"
                    aria-label="Dismiss"
                    data-testid={`notification-delete-${n.id}`}
                  >
                    <Trash size={12} className="text-slate-400 hover:text-red-500" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
        {items.length > 0 && (
          <div className="border-t border-slate-100 px-4 py-2 text-center">
            <button
              onClick={() => { setOpen(false); navigate("/notifications"); }}
              className="text-xs font-medium text-[#1E5CBA] hover:underline"
              data-testid="view-all-notifications"
            >
              View all →
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
